/**
 * Profit-center contractor management page.
 *
 * Lists the contractor orgs affiliated with this profit center (scoped
 * server-side to the PC's own profit center). Contractors are created from
 * their Hajoca (Eclipse) account record and are not editable here, so the
 * platform data stays in lockstep with the ERP; the only per-contractor
 * action is adding an Onsemble user.
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Search, UserPlus } from "lucide-react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuthenticatedQuery } from "@/hooks/use-api";
import { AddUserForm } from "@/components/internal-contractors/AddUserForm";
import { PcAddContractorDialog } from "@/components/pc/PcAddContractorDialog";
import {
  addPcContractorUser,
  PC_CONTRACTORS_QUERY_KEY,
} from "@/requests/pc-contractors";
import type { PcContractorSummary } from "@onsemble/shared/types/pc-contractors";

export default function PcContractors() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [addingUserTo, setAddingUserTo] = useState<PcContractorSummary | null>(
    null,
  );

  const {
    data: contractors,
    isLoading,
    error,
  } = useAuthenticatedQuery<PcContractorSummary[]>([
    ...PC_CONTRACTORS_QUERY_KEY,
  ]);

  const filtered = useMemo(() => {
    const list = contractors ?? [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.hajocaCustomerId?.toLowerCase().includes(q) ?? false),
    );
  }, [contractors, searchQuery]);

  const formatLocation = (c: PcContractorSummary) =>
    [c.address.city, c.address.state].filter(Boolean).join(", ") || "—";

  return (
    <Layout
      headerContent={
        <span className="font-semibold">{t("pages.pcContractors.title")}</span>
      }
    >
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-muted-foreground">
            {t("pages.pcContractors.subtitle", {
              count: contractors?.length ?? 0,
            })}
          </p>
          <Button onClick={() => setCreateOpen(true)}>
            <Building2 className="h-4 w-4 mr-2" />
            {t("pages.pcContractors.addContractor")}
          </Button>
        </div>

        <div className="relative w-full md:max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            type="text"
            placeholder={t("pages.pcContractors.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t("pages.pcContractors.listTitle")}</CardTitle>
            <CardDescription>
              {t("pages.pcContractors.listDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-10 bg-muted rounded animate-pulse" />
                ))}
              </div>
            ) : error ? (
              <p className="text-destructive py-6 text-center">
                {t("pages.pcContractors.loadFailed")}
              </p>
            ) : filtered.length === 0 ? (
              <div className="text-center py-10">
                <Building2 className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="font-medium">
                  {t("pages.pcContractors.emptyTitle")}
                </p>
                <p className="text-muted-foreground text-sm mt-1">
                  {t("pages.pcContractors.emptyDescription")}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        {t("pages.pcContractors.columns.name")}
                      </TableHead>
                      <TableHead>
                        {t("pages.pcContractors.columns.customerId")}
                      </TableHead>
                      <TableHead>
                        {t("pages.pcContractors.columns.phone")}
                      </TableHead>
                      <TableHead>
                        {t("pages.pcContractors.columns.location")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("pages.pcContractors.columns.actions")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((contractor) => (
                      <TableRow key={contractor.orgId}>
                        <TableCell className="font-medium">
                          {contractor.name}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {contractor.hajocaCustomerId ?? "—"}
                        </TableCell>
                        <TableCell>{contractor.phone ?? "—"}</TableCell>
                        <TableCell>{formatLocation(contractor)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setAddingUserTo(contractor)}
                            >
                              <UserPlus className="h-4 w-4 mr-1" />
                              {t("pages.pcContractors.addUser")}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <PcAddContractorDialog open={createOpen} onOpenChange={setCreateOpen} />

      <Dialog
        open={!!addingUserTo}
        onOpenChange={(open) => {
          if (!open) setAddingUserTo(null);
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {t("pages.pcContractors.addUserTitle", {
                orgName: addingUserTo?.name ?? "",
              })}
            </DialogTitle>
            <DialogDescription>
              {t("pages.pcContractors.addUserDescription")}
            </DialogDescription>
          </DialogHeader>
          {addingUserTo && (
            <AddUserForm
              orgId={addingUserTo.orgId}
              orgName={addingUserTo.name}
              submitUser={addPcContractorUser}
              invalidateKey={PC_CONTRACTORS_QUERY_KEY}
              onSuccess={() => setAddingUserTo(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
